import { useState } from 'react';
import { nip19 } from 'nostr-tools';
import { useAuthor } from '@/hooks/useAuthor';
import { FlagSVG, getFlagSVGString } from './FlagSVG';
import { hexToBytes, bytesToFlagData, formatRemainder, contrastColor } from '@/lib/flagUtils';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { Card, CardContent } from '@/components/ui/card';
import { useNostrPublish } from '@/hooks/useNostrPublish';
import { useCurrentUser } from '@/hooks/useCurrentUser';
import { useQueryClient } from '@tanstack/react-query';
import { useToast } from '@/hooks/useToast';
import { generateId } from '@/lib/flagUtils';
import { FLAG_KIND } from '@/hooks/useFlags';
import { createEphemeralKey, signWithEphemeralKey } from '@/lib/ephemeralKeys';
import { useNostr } from '@nostrify/react';
import { Flag, Copy, Check, Download, ArrowLeft, Loader2 } from 'lucide-react';
import { Link } from 'react-router-dom';

interface ProfileFlagPageProps {
  pubkey: string; // 64-char hex
}

export function ProfileFlagPage({ pubkey }: ProfileFlagPageProps) {
  const author = useAuthor(pubkey);
  const metadata = author.data?.metadata;
  const [copied, setCopied] = useState(false);
  const [publishing, setPublishing] = useState(false);

  const { user } = useCurrentUser();
  const { nostr } = useNostr();
  const { mutateAsync: publishEvent } = useNostrPublish();
  const queryClient = useQueryClient();
  const { toast } = useToast();

  const bytes = hexToBytes(pubkey);
  const flagData = bytesToFlagData(bytes);
  const remainderText = formatRemainder(flagData.remainder);

  const displayName = metadata?.name ?? metadata?.display_name ?? (pubkey.slice(0, 8) + '…');
  const npub = nip19.npubEncode(pubkey);
  const formattedHex = pubkey.toUpperCase().replace(/.{2}/g, '$& ').trim();

  async function copyHex() {
    await navigator.clipboard.writeText(formattedHex);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  }

  function downloadSVG() {
    const svg = getFlagSVGString(bytes, 800, 500);
    const blob = new Blob([svg], { type: 'image/svg+xml' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${displayName.replace(/[^a-z0-9]/gi, '_')}_flag.svg`;
    a.click();
    URL.revokeObjectURL(url);
    toast({ title: 'SVG downloaded' });
  }

  async function publishFlag() {
    setPublishing(true);
    try {
      const dTag = generateId();
      const title = `${displayName}'s pubkey flag`;
      const tags: string[][] = [
        ['d', dTag],
        ['title', title],
        ['data', pubkey],
        ['encoding', 'hex'],
        ['alt', `Free Speech Flag: ${title}`],
        ['p', pubkey], // reference the profile
      ];

      const eventTemplate = {
        kind: FLAG_KIND,
        content: title,
        tags,
        created_at: Math.floor(Date.now() / 1000),
      };

      if (user) {
        await publishEvent(eventTemplate);
      } else {
        const ephKey = createEphemeralKey(dTag);
        const signed = signWithEphemeralKey(ephKey, eventTemplate);
        await nostr.event(signed, { signal: AbortSignal.timeout(8000) });
      }

      await queryClient.invalidateQueries({ queryKey: ['flags'] });
      toast({ title: 'Flag published!', description: title });
    } catch (err) {
      toast({ title: 'Failed to publish', description: String(err), variant: 'destructive' });
    } finally {
      setPublishing(false);
    }
  }

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="border-b border-border bg-card/50 backdrop-blur-sm sticky top-0 z-10">
        <div className="max-w-3xl mx-auto px-4 py-3 flex items-center gap-3">
          <Link to="/" className="flex items-center gap-1.5 text-muted-foreground hover:text-foreground transition-colors">
            <ArrowLeft className="h-4 w-4" />
            <Flag className="h-5 w-5" />
            <span className="font-semibold text-sm">Free Speech Flag</span>
          </Link>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-4 py-8 space-y-6">
        {/* Profile header */}
        <div className="flex items-center gap-4">
          {author.isLoading ? (
            <>
              <Skeleton className="h-14 w-14 rounded-full" />
              <div className="space-y-2">
                <Skeleton className="h-5 w-32" />
                <Skeleton className="h-4 w-48" />
              </div>
            </>
          ) : (
            <>
              <Avatar className="h-14 w-14 border-2 border-border">
                <AvatarImage src={metadata?.picture} />
                <AvatarFallback className="text-lg font-bold">{displayName[0]?.toUpperCase()}</AvatarFallback>
              </Avatar>
              <div className="min-w-0">
                <h1 className="text-2xl font-bold text-foreground truncate">{displayName}</h1>
                {metadata?.nip05 && (
                  <p className="text-sm text-muted-foreground truncate">{metadata.nip05}</p>
                )}
                <p className="text-xs text-muted-foreground font-mono truncate">{npub.slice(0, 20)}…</p>
              </div>
            </>
          )}
        </div>

        {/* Flag preview */}
        <div className="rounded-xl overflow-hidden border border-border shadow-sm">
          <FlagSVG bytes={bytes} className="w-full" width={800} height={500} />
        </div>

        {/* Stats */}
        <div className="flex flex-wrap gap-3 text-sm">
          <Badge variant="secondary">{flagData.stripes.length} stripes</Badge>
          <Badge variant="secondary">32 bytes</Badge>
          {remainderText && (
            <Badge variant="outline">Remainder: <code className="ml-1 font-mono">{remainderText}</code></Badge>
          )}
          <Badge variant="outline">Encoding: hex</Badge>
        </div>

        {/* Explanation */}
        <Card className="bg-muted/40">
          <CardContent className="py-4 px-5 text-sm text-muted-foreground space-y-1">
            <p>
              Every Nostr identity is a <strong className="text-foreground">32-byte (256-bit) secp256k1 public key</strong>. 
              Bytes are grouped into <strong className="text-foreground">{flagData.stripes.length} RGB color stripes</strong>, 
              with the remaining <code className="font-mono text-foreground">{remainderText}</code> shown in the bottom-right corner.
            </p>
            <p>
              Each person's flag is <strong className="text-foreground">unique and deterministic</strong> — 
              it cannot be forged or changed without losing the identity.
            </p>
          </CardContent>
        </Card>

        {/* Color breakdown */}
        <div>
          <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground mb-2">Color Breakdown</p>
          <div className="flex flex-wrap gap-2">
            {flagData.stripes.map((stripe, i) => {
              const hex = `#${stripe.r.toString(16).padStart(2,'0').toUpperCase()}${stripe.g.toString(16).padStart(2,'0').toUpperCase()}${stripe.b.toString(16).padStart(2,'0').toUpperCase()}`;
              const textCol = contrastColor(stripe.r, stripe.g, stripe.b);
              return (
                <div
                  key={i}
                  className="flex items-center gap-1.5 rounded-md px-2 py-1 border border-border"
                  style={{ background: hex }}
                >
                  <span className="text-xs font-mono font-semibold" style={{ color: textCol }}>{hex}</span>
                </div>
              );
            })}
            {flagData.remainder.length > 0 && (
              <div className="flex items-center bg-muted rounded-md px-2 py-1 border border-border">
                <span className="text-xs font-mono text-muted-foreground">{remainderText}</span>
              </div>
            )}
          </div>
        </div>

        {/* Hex sequence */}
        <div>
          <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground mb-1">Public Key (hex)</p>
          <div className="bg-muted rounded-md p-3 text-sm font-mono break-all leading-relaxed">
            {formattedHex}
          </div>
        </div>

        {/* Actions */}
        <div className="flex flex-wrap gap-2">
          <Button variant="outline" size="sm" onClick={copyHex}>
            {copied ? <Check className="h-4 w-4 mr-1" /> : <Copy className="h-4 w-4 mr-1" />}
            {copied ? 'Copied!' : 'Copy hex'}
          </Button>
          <Button variant="outline" size="sm" onClick={downloadSVG}>
            <Download className="h-4 w-4 mr-1" />
            Download SVG
          </Button>
          <Button size="sm" onClick={publishFlag} disabled={publishing}>
            {publishing
              ? <><Loader2 className="h-4 w-4 mr-1 animate-spin" /> Publishing…</>
              : <><Flag className="h-4 w-4 mr-1" /> Share to gallery</>
            }
          </Button>
        </div>
      </main>
    </div>
  );
}
